import React, { useState, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import { useFanpages } from '../hooks/useFanpages';
import { useDebounce } from '../hooks/useDebounce';
import { usePagination } from '../hooks/usePagination';
import { applyFanpageFilters } from '../utils/fanpageFilters';
import { getUniqueFanpageTopics } from '../utils/topicClassifier';
import { FanpageCard } from '../components/FanpageCard';
import { SearchBar } from '../components/SearchBar';
import { Pagination } from '../components/Pagination';
import { ResultCount } from '../components/ResultCount';
import { ErrorMessage } from '../components/ErrorMessage';
import { SkeletonGrid } from '../components/SkeletonGrid';
import type { FanpageFilterState } from '../../shared/types';

const FanpagePage: React.FC = () => {
  const { data, isLoading, error, mutate } = useFanpages();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FanpageFilterState['statusFilter']>('Tất cả');
  const [minFollow, setMinFollow] = useState('');
  const [maxFollow, setMaxFollow] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [topicFilter, setTopicFilter] = useState('Tất cả');
  const [bannerOpen, setBannerOpen] = useState(true);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const toNum = (s: string) => s.trim() === '' ? null : Number(s.replace(/\./g, '').replace(/,/g, ''));

  const filterState: FanpageFilterState = {
    searchQuery: debouncedSearch,
    statusFilter,
    minFollow: toNum(minFollow),
    maxFollow: toNum(maxFollow),
    minPrice: toNum(minPrice),
    maxPrice: toNum(maxPrice),
    topicFilter,
    currentPage: 1,
  };

  const allFanpages = data ?? [];
  const topics = getUniqueFanpageTopics(allFanpages);
  const filtered = applyFanpageFilters(allFanpages, filterState);

  const { currentPage, totalPages, setPage, resetPage, paginatedItems } = usePagination(filtered.length);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    resetPage();
  }, [resetPage]);

  const handleStatusChange = (_: React.MouseEvent<HTMLElement>, value: FanpageFilterState['statusFilter'] | null) => {
    if (value !== null) { setStatusFilter(value); resetPage(); }
  };

  const handleTopicClick = (topic: string) => {
    setTopicFilter(topic);
    resetPage();
  };

  /** Format a number string with thousand separators for display */
  const formatDisplayNumber = (s: string): string => {
    const n = Number(s.replace(/\./g, '').replace(/,/g, ''));
    if (isNaN(n) || s.trim() === '') return '';
    return n.toLocaleString('vi-VN');
  };

  const paginated = paginatedItems(filtered);

  return (
    <Box component="main" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>Fanpage</Typography>

      {/* Warranty / info banner */}
      <Collapse in={bannerOpen}>
        <Alert
          severity="info"
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%', overflow: 'hidden' } }}
          action={
            <Button color="inherit" size="small" onClick={() => setBannerOpen(false)} sx={{ whiteSpace: 'nowrap' }}>
              Ẩn
            </Button>
          }
        >
          <strong>⚠️ Lưu ý quan trọng:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20, wordBreak: 'break-word' }}>
            <li>Hỗ trợ đổi tên và xóa bài</li>
            <li>Số like tăng theo ngày — bấm vào link để kiểm tra số like hiện tại trước khi chốt</li>
            <li>Cọc rồi mà 7 ngày không lấy thì mất cọc</li>
            <li>Hàng chính chủ, bảo hành back page vĩnh viễn</li>
            <li>Cam kết like thật, người Việt đang sử dụng 100%</li>
            <li>Liên hệ: <a href="https://www.facebook.com/premium69gr" target="_blank" rel="noopener noreferrer">facebook.com/premium69gr</a></li>
          </ul>
        </Alert>
      </Collapse>

      <section aria-label="Tìm kiếm và lọc fanpage">
        {/* Row 1: Search + Status */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <SearchBar onSearch={handleSearch} ariaLabel="Tìm kiếm fanpage" placeholder="Tìm theo tên fanpage..." />
          </Box>
          <ToggleButtonGroup value={statusFilter} exclusive onChange={handleStatusChange} aria-label="Lọc theo tình trạng" size="small">
            <ToggleButton value="Tất cả" aria-label="Tất cả tình trạng">Tất cả</ToggleButton>
            <ToggleButton value="còn" aria-label="Tình trạng còn">Còn</ToggleButton>
            <ToggleButton value="hết" aria-label="Tình trạng hết">Hết</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Row 2: Advanced filters — open by default */}
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={true}
          sx={{ border: '1px solid', borderColor: 'divider', mb: 2, borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<span>▼</span>} aria-controls="advanced-filter-content">
            <Typography variant="body2" color="text.secondary">Bộ lọc nâng cao</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Topic chips */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Chủ đề
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    gap: 1,
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'divider' },
                  }}
                >
                  {['Tất cả', ...topics].map(topic => (
                    <Chip
                      key={topic}
                      label={topic}
                      size="small"
                      onClick={() => handleTopicClick(topic)}
                      color={topicFilter === topic ? 'primary' : 'default'}
                      variant={topicFilter === topic ? 'filled' : 'outlined'}
                      sx={{ flexShrink: 0, cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Follow range */}
                <Box>
                  <Typography variant="caption" color="text.secondary">Follower</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                    <TextField
                      label="Tối thiểu"
                      size="small"
                      value={minFollow}
                      onChange={e => { setMinFollow(e.target.value); resetPage(); }}
                      inputProps={{ 'aria-label': 'Follower tối thiểu', min: 0 }}
                      sx={{ width: 130 }}
                    />
                    {minFollow && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDisplayNumber(minFollow)}
                      </Typography>
                    )}
                    <TextField
                      label="Tối đa"
                      size="small"
                      value={maxFollow}
                      onChange={e => { setMaxFollow(e.target.value); resetPage(); }}
                      inputProps={{ 'aria-label': 'Follower tối đa', min: 0 }}
                      sx={{ width: 130 }}
                    />
                    {maxFollow && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDisplayNumber(maxFollow)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Price range */}
                <Box>
                  <Typography variant="caption" color="text.secondary">Giá (đ)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                    <TextField
                      label="Tối thiểu"
                      size="small"
                      value={minPrice}
                      onChange={e => { setMinPrice(e.target.value); resetPage(); }}
                      inputProps={{ 'aria-label': 'Giá tối thiểu', min: 0 }}
                      sx={{ width: 130 }}
                    />
                    {minPrice && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDisplayNumber(minPrice)} đ
                      </Typography>
                    )}
                    <TextField
                      label="Tối đa"
                      size="small"
                      value={maxPrice}
                      onChange={e => { setMaxPrice(e.target.value); resetPage(); }}
                      inputProps={{ 'aria-label': 'Giá tối đa', min: 0 }}
                      sx={{ width: 130 }}
                    />
                    {maxPrice && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDisplayNumber(maxPrice)} đ
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </section>

      {isLoading && <SkeletonGrid count={8} />}

      {error && !isLoading && (
        <ErrorMessage message="Không thể tải dữ liệu fanpage." onRetry={() => mutate()} />
      )}

      {!isLoading && !error && (
        <section aria-label="Danh sách fanpage">
          <ResultCount count={filtered.length} />
          {filtered.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>Không tìm thấy fanpage nào phù hợp.</Typography>
          ) : (
            <Grid container spacing={2}>
              {paginated.map((fp, idx) => (
                <Grid item key={fp.link || fp.name + idx} xs={12} sm={6} md={4} lg={3}>
                  <FanpageCard fanpage={fp} />
                </Grid>
              ))}
            </Grid>
          )}
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} onPageChange={setPage} />
        </section>
      )}
    </Box>
  );
};

export default FanpagePage;
